import classnames from 'classnames';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';

import { useTrackViewedElement } from '@/core/tracking/hooks/useTrackViewedElement';
import { Gtm } from '@/core/tracking/utils/Gtm';
import { ChipLink } from '@/productDiscovery/Listings/components/ChipLink/ChipLink';
import { ListingLayoutCarousel } from '@/productDiscovery/Listings/components/ListingLayoutCarousel/ListingLayoutCarousel';
import type { SmartLink } from '@/productDiscovery/microservices/msfProductListing/interfaces/processedData';

import type { SmartLinkClicked, SmartLinksViewed } from './interfaces';
import { title } from './translations';

import styles from './SmartLinks.module.scss';

export interface SmartLinksProps {
  className?: string;
  smartLinks: SmartLink[];
}

export const SmartLinks: FunctionComponent<SmartLinksProps> = ({
  className,
  smartLinks,
}) => {
  const rootClassnames = classnames(styles.root, className);

  const { push } = useRouter();

  const [ref] = useTrackViewedElement(() => {
    Gtm.push<SmartLinksViewed>({
      event: 'interaction_display smart links block',
      e_category: 'navigation',
      e_action: 'Display smart links block',
      e_label: smartLinks.map(({ name }) => name),
      event_trigger: 'category_page',
      amp_event_name: 'Display smart links block',
      query_label: smartLinks.map(({ name }) => name),
      query_type: undefined,
      query_position: undefined,
      query_count: smartLinks.length,
      interaction_hit: 'True',
    });
  });

  const handleRedirection = (smartLink: SmartLink) => {
    push(smartLink.url);

    Gtm.push<SmartLinkClicked>({
      event: 'interaction_click on smart links block',
      e_category: 'navigation',
      e_action: 'Click on smart links block',
      e_label: smartLink.name,
      event_trigger: 'category_page',
      amp_event_name: 'Click on smart links block',
      query_label: smartLink.name,
      query_type: 'smart_links',
      query_position: smartLinks.findIndex(
        ({ name }) => name === smartLink.name,
      ),
      query_count: undefined,
      interaction_hit: 'False',
    });
  };

  return (
    <div ref={ref} className={rootClassnames}>
      <ListingLayoutCarousel
        className={styles.smartLinks}
        title={title}
        titleClassName={styles.title}
        controlsClassName={styles.controls}
        sliderClassName={styles.slider}
      >
        {smartLinks.map((smartLink) => (
          <ChipLink
            id={smartLink.url}
            key={smartLink.name}
            label={smartLink.name}
            onClick={() => handleRedirection(smartLink)}
            href={smartLink.url}
            theme="outline"
          />
        ))}
      </ListingLayoutCarousel>
    </div>
  );
};

SmartLinks.displayName = 'SmartLinks';
